import { SupportedLanguage } from '../../i18n/locales';
import { getEnvVar } from '../env';

export type Locale = {
  default: boolean;
  language: string;
  country: string;
  localeId: string;
  locale: SupportedLanguage;
};

/**
 * Retrieves an array of enabled locales from environment variables.
 *
 * @returns {string[]} An array of enabled locale codes. Returns an empty array if no locales are configured.
 */
export const getEnabledLocales = (): string[] => {
  const enabledLocales: string = (
    getEnvVar('NEXT_PUBLIC_ENABLED_LOCALES') ?? ''
  ).trim();
  const enabledLocalesArray: string[] =
    enabledLocales.length > 0
      ? enabledLocales.split(',').map((locale: string) => locale.trim())
      : [];
  return enabledLocalesArray;
};

/**
 * Sorts an array of Locale objects based on default status and language.
 * Default locales are moved to the beginning of the array.
 * Non default locales are sorted alphabetically by language.
 *
 * @param locales - Array of Locale objects to be sorted
 * @returns A new sorted array of Locale objects
 */
export const sortLocales = (locales: Locale[]): Locale[] => {
  return locales.sort((a, b) => {
    if (a.default) return -1;
    if (b.default) return 1;
    if (a.language < b.language) return -1;
    if (a.language > b.language) return 1;
    return 0;
  });
};

/**
 * Filters an array of locales based on enabled locale IDs.
 * If enabledLocalesArray is empty, returns the original array unfiltered.
 * Otherwise returns locales that are either default or have their ID in enabledLocalesArray.
 *
 * @param locales - Array of Locale objects to filter
 * @param enabledLocalesArray - Array of locale IDs that should be included in the results
 * @returns Filtered array of Locale objects
 */
export const filterLocales = (
  locales: Locale[],
  enabledLocalesArray: string[],
): Locale[] => {
  return enabledLocalesArray.length > 0
    ? locales.filter(
        (locale) =>
          locale.default || enabledLocalesArray.includes(locale.localeId),
      )
    : locales;
};

/**
 * Formats an array of locale objects by transforming their locale IDs into a simplified format.
 *
 * For locales with the same language code but different country codes (e.g., "en_US" and "en_GB"),
 * the function keeps the country code in lowercase (e.g., "en_us", "en_gb").
 * For unique language codes, it simplifies to just the language code (e.g., "en_US" becomes "en").
 *
 * @param locales - Array of Locale objects containing localeId and other properties
 * @returns Array of formatted Locale objects with additional 'locale' property
 */
export const formatLocales = (locales: Locale[]): Locale[] => {
  return locales.map((locale) => {
    const [languageCode, countryCode] = locale.localeId.split('_') as [
      string,
      string,
    ];
    const isDuplicate = locales.some(
      (otherLocale) =>
        otherLocale.localeId.startsWith(languageCode) && otherLocale !== locale,
    );
    // convert java locale "en_US" into shortform "en", or for multiple variants "en_us"
    const formattedLocale: SupportedLanguage = (
      isDuplicate
        ? `${languageCode}_${countryCode.toLowerCase()}`
        : languageCode
    ) as SupportedLanguage;

    return {
      ...locale,
      locale: formattedLocale,
    };
  });
};

const ISO_639_2_TO_1: Record<string, string> = {
  eng: 'en',
  swe: 'sv',
  dan: 'da',
  pol: 'pl',
  fra: 'fr',
  fre: 'fr',
  deu: 'de',
  ger: 'de',
  nld: 'nl',
  dut: 'nl',
  spa: 'es',
  ita: 'it',
  por: 'pt',
  rus: 'ru',
  jpn: 'ja',
  kor: 'ko',
  zho: 'zh',
  chi: 'zh',
  ara: 'ar',
  ces: 'cs',
  cze: 'cs',
  tur: 'tr',

  // Norwegian
  nor: 'nb',
  nob: 'nb',
  nno: 'nb',
};

/**
 * Converting ISO-639-2 (3-letter) language codes from Bitmovin to ISO-639-1 (2-letter) locale codes.
 * @param lang - The audio language code to normalize.
 * @returns The normalized ISO-639-1 locale code, or undefined if input is invalid or unknown.
 */
export function normalizeAudioLang(lang?: string) {
  if (!lang) return undefined;

  const normalized = lang.toLowerCase();

  if (ISO_639_2_TO_1[normalized]) {
    return ISO_639_2_TO_1[normalized];
  }

  // If already ISO-639-1
  if (normalized.length === 2) {
    return normalized;
  }

  return undefined;
}
